import React, { useCallback, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { isAxiosError } from "axios";
import { format, isAfter, milliseconds } from "date-fns";
import { identity, orderBy, uniq } from "lodash";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom-v5-compat";

import useAddTradeProposalToPrimaryBundle from "@fartherfinance/frontend/api/Investment/hooks/useAddTradeProposalToPrimaryBundle";
import useGetExcludedAssets from "@fartherfinance/frontend/api/Investment/hooks/useGetExcludedAssets";
import useGetProposal from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import useGetProposalPostTradeHoldings from "@fartherfinance/frontend/api/Investment/hooks/useGetProposalPostTradeHoldings";
import { TradeProposalId } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposals";
import useGetTlhSwapsAcceptedByProposalId from "@fartherfinance/frontend/api/Investment/hooks/useGetTlhSwapsAcceptedByProposalId";
import usePortfolioPlanAssignment from "@fartherfinance/frontend/api/Investment/hooks/usePortfolioPlanAssignment";
import usePostTradeProposalSkip from "@fartherfinance/frontend/api/Investment/hooks/usePostTradeProposalSkip";
import {
  TradeProposalBundleId,
  TradeProposalBundleType,
} from "@fartherfinance/frontend/api/Investment/types";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { SecurityId } from "@fartherfinance/frontend/api/Types";

import { useFinOpsPermission } from "../FinOpsPermissionContext";
import { BundleLocationState, ProposalLocationState } from "../types";
import useEquivalentSecurityAllMembersWithInfo from "@src/multiCustodian/components/Client/EquivalentSecurities/useEquivalentSecurityAllMembersWithInfo";
import { EquivalentSecurityTransformerId } from "@src/multiCustodian/components/Client/Portfolio/TradingGroupsTable/TradingGroupsTableV2";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import AlertInline from "@src/sharedComponents/AlertInline/AlertInline";
import Card from "@src/sharedComponents/Card/Card";
import Chip from "@src/sharedComponents/Chip/Chip";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import Text from "@src/sharedComponents/Typography/Text";
import { pluralize } from "@src/utils/pluralize";

import ProposalAlerts from "./ProposalAlerts/ProposalAlerts";
import CardAccount from "./ProposalDetails/CardAccount";
import CardPortfolioPlanAssignment from "./ProposalDetails/CardPortfolioPlanAssignment";
import CardPortfolioPlanAssignmentStatus from "./ProposalDetails/CardPortfolioPlanAssignmentStatus";
import CardUser from "./ProposalDetails/CardUser";
import createEquivalentSecurityAndEsListLookup, {
  emptyLookup,
} from "./ProposalDetails/createEquivalentSecurityAndEsListLookup";
import PostTradeHoldings from "./ProposalDetails/PostTradeHoldings";
import constructPostTradeCSVData from "./ProposalDetails/postTradePortfolioCsvData";
import constructCSVDataProposedTrades from "./ProposalDetails/proposedTradesCsvData";
import RelatedTrades from "./ProposalDetails/RelatedTrades/RelatedTrades";
import TradeConsequences from "./ProposalDetails/TradeConsequences";
import TradeList from "./ProposalDetails/TradeList";
import TradeTotals from "./ProposalDetails/TradeTotals";
import useProposalAlerts from "./useProposalAlerts.hooks";
import useSecuritiesByTradeProposal from "./useSecuritiesByTradeProposal.hooks";

import dnsStyles from "./ProposalDetails/DnsIndicator.module.css";
import styles from "./ProposalDetails/ProposalDetails.module.css";

interface Props {
  tradeProposalId: TradeProposalId;
  nextProposalId?: TradeProposalId;
}

type LocationState = ProposalLocationState | BundleLocationState;

function isProposalLocationState(
  state: ProposalLocationState | BundleLocationState
): state is ProposalLocationState {
  return state !== undefined && "proposalIds" in state;
}

const formatMMDDYYYY = (date: Date): string => {
  return format(date, "MM-dd-yyyy");
};

const CSV_MIME_TYPE = "text/csv;charset=utf-8;";

const createCsvBlob = (csvContent: string) => {
  return new Blob([csvContent], { type: CSV_MIME_TYPE });
};

const downloadCsv = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const ProposalDetails: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const { state, pathname } = useLocation<LocationState>(); // We need to keep propagating the state to allow next/skip button

  const [isMutating, setIsMutating] = useState(false);
  const notification = useStatusNotification();

  const hasUpdatePermission = useFinOpsPermission();

  const auth = useAdvisorRequestAuth();
  const proposal = useGetProposal(props.tradeProposalId, auth, false, {
    refetchInterval: milliseconds({ seconds: 10 }),
  });

  const skipProposalOnServer = usePostTradeProposalSkip(auth);

  const tradeSecurityRequest = useSecuritiesByTradeProposal(
    props.tradeProposalId
  );

  const postTradeHoldingsReq = useGetProposalPostTradeHoldings(
    props.tradeProposalId,
    auth
  );

  const allHoldingSecIds = useMemo((): SecurityId[] | null => {
    if (postTradeHoldingsReq.data === undefined) {
      return null;
    }

    // Order them as the server does not guarantee stable ordering of holdings
    const secIds = orderBy(
      postTradeHoldingsReq.data.map((holding) => holding.securityId),
      [identity],
      ["asc"]
    );

    if (secIds.length === 0) {
      return [];
    }

    return uniq(secIds);
  }, [postTradeHoldingsReq.data]);

  const holdingSecurityRequest = useSecuritiesBySecurityIds(
    allHoldingSecIds,
    auth
  );

  // Fetch excluded assets (Do Not Sell securities) for the current portfolio plan assignment
  const excludedAssets = useGetExcludedAssets(
    proposal?.data?.portfolioPlanAssignment.portfolioPlanId ?? null,
    proposal?.data?.portfolioPlanAssignment.planAssignmentId ?? null,
    auth
  );

  // Filter excluded assets to only show those created before the proposal date
  const filteredExcludedAssets = useMemo(() => {
    if (!excludedAssets.data || !proposal.data?.createdAt) {
      return [];
    }

    return excludedAssets.data.filter(
      (asset) => asset.createdAt && asset.createdAt < proposal.data!.createdAt
    );
  }, [excludedAssets.data, proposal.data]);

  // Fetch accepted TLH swaps for the current trade proposal
  const clientId = proposal.data?.clientIds?.[0];
  const tlhSwaps = useGetTlhSwapsAcceptedByProposalId(
    props.tradeProposalId,
    clientId ?? null,
    auth
  );

  const acceptedTLHSwaps = tlhSwaps.data ?? [];

  const addToPrimaryBundleApi = useAddTradeProposalToPrimaryBundle(auth);

  const addToPrimaryBundle = useCallback(
    async (tradeProposalId: TradeProposalId, type: TradeProposalBundleType) => {
      try {
        setIsMutating(true);

        await addToPrimaryBundleApi({
          tradeProposalIds: [tradeProposalId],
          type: type,
        });

        notification("Trade added to primary bundle", "Success");
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 400) {
          const errorMessage = (
            e.response.data as unknown as { message: string }
          ).message;
          notification(errorMessage, "Error");
        } else {
          console.error(e);

          notification("Failed to add trade to primary bundle", "Error");
        }
      } finally {
        setIsMutating(false);
      }
    },
    [addToPrimaryBundleApi, notification]
  );

  const canNavigateToNextProposal = useMemo(
    () => props.nextProposalId !== undefined,
    [props.nextProposalId]
  );

  const nextProposal = useCallback(() => {
    if (props.nextProposalId !== undefined) {
      const url = `/Advisor/FinancialOperations/TradeProposals/${props.nextProposalId}`;
      navigate(url, { state });
    }
  }, [navigate, props.nextProposalId, state]);

  const skipAProposal = useCallback(
    async (tradeProposalId: TradeProposalId) => {
      try {
        setIsMutating(true);

        await skipProposalOnServer(tradeProposalId);

        notification("Trade skipped", "Success");

        nextProposal();
      } catch (e) {
        console.error(e);
        notification("Failed to skip trade", "Error");
      } finally {
        setIsMutating(false);
      }
    },
    [nextProposal, notification, skipProposalOnServer]
  );

  const inBundle: TradeProposalBundleId | null = useMemo(() => {
    if (proposal.data === undefined) {
      return null;
    }

    // Already in
    if (proposal.data.tradeProposalBundle !== null) {
      return proposal.data.tradeProposalBundle.bundleId;
    }

    return null;
  }, [proposal.data]);

  const isExpired: boolean | null = useMemo(() => {
    if (proposal.data === undefined) {
      return null;
    }

    // Null expiration means hypothetical proposal and we can't submit
    if (proposal.data.expiresAt === null) {
      return true;
    }

    return isAfter(new Date(), proposal.data.expiresAt);
  }, [proposal.data]);

  const proposalCanBeSubmitted: boolean = useMemo(() => {
    if (proposal.data === undefined) {
      return false;
    }

    // Already in a bundle, can't re-submit
    if (inBundle !== null) {
      return false;
    }

    if (isExpired) {
      return false;
    }

    // This proposal was already skipped, can't submit it
    if (proposal.data.status === "Skipped") {
      return false;
    }

    // This proposal was already submitted, can't re-submit it
    if (proposal.data.status === "Submitted") {
      return false;
    }

    // This proposal failed and we don't trust its internal state
    if (proposal.data.status === "Failed") {
      return false;
    }

    // This proposal was cancelled and we don't trust its internal state
    if (proposal.data.status === "Canceled") {
      return false;
    }

    return true;
  }, [inBundle, isExpired, proposal.data]);

  const isImmaterial = proposal.data?.status === "Immaterial";

  const onNavigateBack = useCallback(() => {
    // Navigate back to the Trade Proposals page while preserving relevant state
    const tradeProposalURL = "/Advisor/FinancialOperations/TradeProposals";

    // if we have TradeProposalBundles in the path, we know this should be Advisor/FinancialOperations/TradeProposalBundles/TPB_X/TRP_Y
    // and we want to go back to Advisor/FinancialOperations/TradeProposalBundles/TPB_X
    const navigateBackURL = pathname.includes("TradeProposalBundles")
      ? pathname.slice(0, pathname.lastIndexOf("/"))
      : tradeProposalURL;

    let newState: ProposalLocationState | BundleLocationState = {};

    if (isProposalLocationState(state)) {
      const { proposalIds: _, ...rest } = state ?? {};

      newState = { ...rest };
    }

    navigate(navigateBackURL, { state: newState });
  }, [navigate, pathname, state]);

  const alertsArray = useProposalAlerts(proposal.data);

  const downloadProposedTradesCsv = () => {
    if (proposal.data === undefined) {
      return;
    }

    const finalCsv = constructCSVDataProposedTrades({
      proposal: proposal.data,
      securityDb: tradeSecurityRequest.data,
    });

    const csvBlob = createCsvBlob(finalCsv);

    const date = formatMMDDYYYY(proposal.data.createdAt);
    const fileName = `proposed-trades_${date}_${props.tradeProposalId}.csv`;

    downloadCsv(csvBlob, fileName);
  };

  const downloadPostTradePortfolioCsv = () => {
    if (proposal.data === undefined) {
      return;
    }

    const finalCsv = constructPostTradeCSVData({
      postTradeHoldings: postTradeHoldingsReq.data,
      securityDb: holdingSecurityRequest.data,
    });

    const csvBlob = createCsvBlob(finalCsv);

    const date = formatMMDDYYYY(proposal.data.createdAt);
    const fileName = `post-trade-portfolio_${date}_${props.tradeProposalId}.csv`;

    downloadCsv(csvBlob, fileName);
  };

  const listsRequest = useEquivalentSecurityAllMembersWithInfo();

  const esLookup = useMemo(() => {
    if (proposal.data === undefined || proposal.data.trades.length === 0) {
      return emptyLookup;
    }

    if (listsRequest.data === undefined) {
      return emptyLookup;
    }

    return createEquivalentSecurityAndEsListLookup(
      proposal.data.trades,
      listsRequest.data
    );
  }, [listsRequest.data, proposal.data]);

  const esLookupPostTradeModel = useMemo(() => {
    if (
      postTradeHoldingsReq.data === undefined ||
      postTradeHoldingsReq.data.length === 0
    ) {
      return emptyLookup;
    }

    if (listsRequest.data === undefined) {
      return emptyLookup;
    }

    return createEquivalentSecurityAndEsListLookup(
      postTradeHoldingsReq.data,
      listsRequest.data
    );
  }, [listsRequest.data, postTradeHoldingsReq.data]);

  const params = useMemo(
    () =>
      proposal.data?.portfolioPlanAssignment.planAssignmentId === undefined ||
      proposal.data?.portfolioPlanAssignment.portfolioPlanId === undefined
        ? null
        : {
            abstractPortfolioId:
              proposal.data?.portfolioPlanAssignment.portfolioPlanId,
            portfolioPlanAssignmentId:
              proposal.data?.portfolioPlanAssignment.planAssignmentId,
          },
    [proposal.data]
  );

  const assignment = usePortfolioPlanAssignment(params, auth);

  const isEsEnabled = useMemo(
    () =>
      (proposal.data?.portfolioPlanAssignment.transformers ?? []).some(
        (tfmr) => tfmr.transformerId === EquivalentSecurityTransformerId
      ),
    [proposal.data]
  );

  const esInProposedTradesText = `${pluralize(
    Object.keys(esLookup.securities).length,
    "security",
    true
  )} in ${pluralize(Object.keys(esLookup.lists).length, "list", true)}`;

  const esInPostTradeModelText = `${pluralize(
    Object.keys(esLookupPostTradeModel.securities).length,
    "security",
    true
  )} in ${pluralize(
    Object.keys(esLookupPostTradeModel.lists).length,
    "list",
    true
  )}`;

  // Check if the version of the assignment matches the version of the assignment from/in the trade proposal
  // If they don't match, we need to show a warning to the user
  const versionDoesNotMatch = useMemo(() => {
    if (assignment.data !== undefined && proposal.data !== undefined) {
      return (
        assignment.data.version !==
        proposal.data.portfolioPlanAssignment.version
      );
    }
  }, [assignment.data, proposal.data]);

  return (
    <>
      <div id="proposal-details-header" className={styles.Header}>
        <Button
          text="Return to table"
          variant="outlined"
          buttonType="secondary"
          onClick={onNavigateBack}
        />

        <div className={styles.SideBySide}>
          <div className={styles.FlexRow}>
            <span>Proposed</span>:&nbsp;
            {proposal.data === undefined ? (
              <Skeleton width={150} />
            ) : (
              <span>
                {format(proposal.data.createdAt, "M/d/yyyy - hh:mm a")}
              </span>
            )}
          </div>

          <div className={styles.FlexRow}>
            <span>Expires</span>:&nbsp;
            {proposal.data === undefined ? (
              <Skeleton width={150} />
            ) : proposal.data.expiresAt === null ? (
              <span>Never</span>
            ) : (
              <span>
                {format(proposal.data.expiresAt, "M/d/yyyy - hh:mm a")}
              </span>
            )}
          </div>
        </div>

        <div className={styles.SideBySide}>
          <Button
            text="Next Trade"
            variant="contained"
            buttonType="primary"
            disabled={canNavigateToNextProposal === false}
            onClick={() => nextProposal()}
          />
        </div>
      </div>

      <div className={styles.VerticalSeparator} />

      <div id="proposal-details-top-row" className={styles.TopRow}>
        <CardUser clientIds={proposal.data?.clientIds} />

        <CardAccount
          accountIds={proposal.data?.portfolioPlanAssignment.accounts}
          tradeProposalId={props.tradeProposalId}
        />

        <CardPortfolioPlanAssignment
          portfolioPlanAssignmentId={
            proposal.data?.portfolioPlanAssignment.planAssignmentId
          }
          portfolioPlanId={
            proposal.data?.portfolioPlanAssignment.portfolioPlanId
          }
          proposalId={props.tradeProposalId}
          proposalCreatedAt={proposal.data?.createdAt}
          filteredExcludedAssets={filteredExcludedAssets}
          equivalentSecuritiesEnabled={
            versionDoesNotMatch ? undefined : isEsEnabled
          }
        />

        <CardPortfolioPlanAssignmentStatus
          proposalId={props.tradeProposalId}
          portfolioPlanId={
            proposal.data?.portfolioPlanAssignment.portfolioPlanId
          }
          portfolioPlanAssignmentId={
            proposal.data?.portfolioPlanAssignment.planAssignmentId
          }
        />
      </div>

      <div className={styles.VerticalSeparator} />

      <>
        <div id="proposal-details-top-row" className={styles.TopRow}>
          {isExpired ? (
            <Card header="Expired">
              <Text>This proposal is expired</Text>
            </Card>
          ) : null}

          {isImmaterial ? (
            <Card header="Immaterial">
              <Text>This proposal is immaterial</Text>
            </Card>
          ) : null}

          {proposal.data?.status === "Skipped" ? (
            <Card header="Skipped">
              <Text>This proposal was skipped</Text>
            </Card>
          ) : null}

          {proposal.data?.status === "Submitted" ? (
            <Card header="Submitted">
              <Text>This proposal was already submitted</Text>
            </Card>
          ) : null}
        </div>

        <div className={styles.VerticalSeparator} />
      </>

      <ProposalAlerts alerts={alertsArray} />

      {isEsEnabled && (
        <div className={styles.DoNotSellLegend}>
          <div className={dnsStyles.legendContainer}>
            <Chip size="small" label="ES" className={dnsStyles.eqivSecsChip} />
            <Text variant="body">
              {`= Equivalent Securities (Securities that are considered
              equivalent) - Proposed Trades: ${esInProposedTradesText} - Post Trade Model: ${esInPostTradeModelText}`}
            </Text>
          </div>
        </div>
      )}

      {filteredExcludedAssets.length > 0 && (
        <div className={styles.DoNotSellLegend}>
          <div className={dnsStyles.legendContainer}>
            <Chip size="small" label="DNS" className={dnsStyles.dnsChip} />
            <Text variant="body">
              = Do Not Sell (Excluded assets that are protected from selling)
            </Text>
          </div>
        </div>
      )}

      {acceptedTLHSwaps.length > 0 && (
        <div className={styles.DoNotSellLegend}>
          <div className={dnsStyles.legendContainer}>
            <Chip size="small" label="TLH" className={dnsStyles.dnsChip} />
            <Text variant="body">
              = Tax Loss Harvest (Securities involved in tax-loss harvesting
              swaps)
            </Text>
          </div>
        </div>
      )}

      {/* only show related proposals for proposals that can be submitted */}
      <Text variant="title1">Related Trades</Text>

      {proposal.data?.status === "Proposed" ? (
        <RelatedTrades tradeProposalId={props.tradeProposalId} />
      ) : (
        <AlertInline
          severity="info"
          message="Related trades are only available for proposals that can be submitted"
        />
      )}
      <div className={styles.VerticalSeparator} />

      <Stack flexDirection={"row"} justifyContent={"space-between"}>
        <Text variant="title1">Proposed Trades</Text>

        <Button
          text="Export to CSV"
          variant="text"
          buttonType="secondary"
          disabled={proposal.data === undefined}
          onClick={downloadProposedTradesCsv}
        />
      </Stack>

      <TradeList
        proposal={proposal.data}
        securityDb={tradeSecurityRequest.data}
        excludedAssets={filteredExcludedAssets}
        acceptedTLHSwaps={acceptedTLHSwaps}
        esLookup={isEsEnabled ? esLookup : undefined}
      />

      <div className={styles.VerticalSeparator} />

      <Text variant="title1">Proposed Trade Totals</Text>

      <TradeTotals proposal={proposal.data} />

      <div className={styles.VerticalSeparator} />

      <Text variant="title1">Post-Trade Consequences</Text>

      <TradeConsequences proposal={proposal.data} />

      <div className={styles.VerticalSeparator} />

      <Stack flexDirection={"row"} justifyContent={"space-between"}>
        <Text variant="title1">Post-Trade Portfolio</Text>

        <Button
          text="Export to CSV"
          variant="text"
          buttonType="secondary"
          disabled={holdingSecurityRequest.data === undefined}
          onClick={downloadPostTradePortfolioCsv}
        />
      </Stack>

      <PostTradeHoldings
        securityDb={holdingSecurityRequest.data}
        postTradeHoldings={postTradeHoldingsReq.data}
        excludedAssets={filteredExcludedAssets}
        acceptedTLHSwaps={acceptedTLHSwaps}
        hasError={
          holdingSecurityRequest.hasError || postTradeHoldingsReq.hasError
        }
        esLookup={isEsEnabled ? esLookupPostTradeModel : undefined}
      />

      <div className={styles.VerticalSeparator} />
      <div className={styles.VerticalSeparator} />

      <div id="proposal-details-bottom-row" className={styles.BottomRow}>
        <div>
          {isExpired ? (
            <div>This proposal is expired and can't be submitted.</div>
          ) : null}

          {inBundle !== null ? (
            <div>
              This proposal is in bundle{" "}
              <a
                href={`/Advisor/FinancialOperations/TradeProposalBundles/${inBundle}`}
              >
                {inBundle}
              </a>{" "}
              and can't be re-submitted.
            </div>
          ) : null}

          {proposal.data?.status === "Skipped" ? (
            <div>This proposal was skipped.</div>
          ) : null}
        </div>

        <div className={[styles.FlexRow, styles.Gap10].join(" ")}>
          <Button
            text="Skip"
            variant="outlined"
            buttonType="secondary"
            disabled={
              !hasUpdatePermission ||
              isMutating ||
              canNavigateToNextProposal === false ||
              proposal.data?.status === "Skipped" // Can't re-skip
            }
            onClick={() => skipAProposal(props.tradeProposalId)}
          />

          <Button
            text="Needs Review"
            variant="contained"
            buttonType="primary"
            disabled={
              !hasUpdatePermission ||
              isMutating ||
              proposalCanBeSubmitted === false
            }
            onClick={() =>
              addToPrimaryBundle(
                props.tradeProposalId,
                "RequiresAdditionalReview"
              )
            }
          />

          <Button
            text="Confirm Trade"
            variant="contained"
            buttonType="info"
            disabled={
              !hasUpdatePermission ||
              isMutating ||
              proposalCanBeSubmitted === false
            }
            onClick={() =>
              addToPrimaryBundle(props.tradeProposalId, "Standard")
            }
          />
        </div>
      </div>
    </>
  );
};

export default ProposalDetails;
